import React, { useEffect, useState } from 'react';
import './LoginS.scss';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { googleIcon, logoImg } from '../../utils/images';
import { useAuth } from '../../contexts/AuthContext';

export const LoginFormS: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const { session, role } = useAuth();
  const navigate = useNavigate();

  const [pendingRedirect, setPendingRedirect] = useState(false);

  useEffect(() => {
    if (session && role !== null) {
      navigate(role === 'personal' ? '/my-displays' : '/stores', { replace: true });
    }
  }, [pendingRedirect, session, role, navigate]);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email');
      setIsLoading(false);
      return;
    }
    
    if (!password) {
      setError('Please enter your password');
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });
      if (error) throw error;
      setPendingRedirect(true);
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.error_description || error.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
      });
      if (error) throw error;
      // Redirect happens automatically, session update handled by onAuthStateChange
    } catch (error: any) {
        console.error('Google Login error:', error);
        setError(error.error_description || error.message || 'Google login failed.');
        setIsLoading(false); // Only set loading false if there's an immediate error
    }
  };

  return (
      <div className="login">
        {/* Language selector - kept from original Login.tsx */}
        {/* <div className="language-selector">
          <button 
            className="language-selector__button"
            onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          >
            <span className="language-selector__icon">🌐</span>
          </button>
          {showLanguageDropdown && (
            <div className="language-selector__dropdown">
              <div className="language-selector__option language-selector__option--selected">
                <span className="language-selector__check">✓</span>
                English
              </div>
            </div>
          )}
        </div> */}
        
        <div className="login__container">
          <div className="login__t">
            <div className="login__logo-wrapper">
              <img src={logoImg} alt="Minew ESL Cloud Platform" className="login__logo" />
            </div>
            
            {/* Replicated form structure from Login.tsx */}
            <form className="login-form" onSubmit={handlePasswordLogin}>
              <div className="login-form__field">
                <span className="login-form__icon">👤</span>
                <input
                  type="email" // Use type="email" for better validation/UX
                  className="login-form__input"
                  placeholder="Email" // Changed placeholder
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required // Added required attribute
                />
              </div>
            
              <div className="login-form__field">
                <span className="login-form__icon">🔒</span>
                <input
                  type="password"
                  className="login-form__input"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required // Added required attribute
                />
              </div>
            
              {/* Removed "Remember Me" checkbox - Supabase handles sessions */}
              <div className="login-form__options">
                 {/* Removed remember me div */}
                 {/* "Forgot Password?" link */}
                <Link to="/forgot-password" className="login-form__forgot">Forgot Password?</Link>
              </div>

              {/* Display error message if any */}
              {error && <p className="login-form__error">{error}</p>}
            
              <button
                type="submit"
                className="login-form__submit"
                disabled={isLoading}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
            
              <div className="login-form__divider">
                <span className="login-form__divider-text">- - or - -</span>
              </div>
            
              {/* Google Login Button */}
              <button 
                type="button" 
                className="login-form__google" 
                onClick={handleGoogleLogin}
                disabled={isLoading}
              >
                <img src={googleIcon} alt="Google" className="login-form__google-icon" />
                Login with Google
              </button>

              <p className="login-form__signup-link">
                Don't have an account? <Link to="/sign-up">Sign up</Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    );
};
